"use client";

import { createContext, useCallback, useContext, useLayoutEffect, useMemo, useState } from "react";
import { usePathname } from "next/navigation";
import { MotionConfig } from "framer-motion";
import {
  DEFAULT_LOCALE,
  dictionaries,
  dirFor,
  interpolate,
  isLocale,
  LOCALE_KEY,
  localeMeta,
  tabTitle,
  THEME_KEY,
  type Dict,
  type Dir,
  type Locale,
  type Theme,
} from "@/lib/i18n";

type Prefs = {
  locale: Locale;
  dir: Dir;
  /** BCP-47 tag for Intl.* formatting. */
  intl: string;
  theme: Theme;
  t: Dict;
  setLocale: (locale: Locale) => void;
  setTheme: (theme: Theme) => void;
  toggleTheme: () => void;
  tf: (template: string, vars: Record<string, string | number>) => string;
};

const PrefsContext = createContext<Prefs | null>(null);

function applyDom(locale: Locale, theme: Theme) {
  const root = document.documentElement;
  root.lang = locale;
  root.dir = dirFor(locale);
  root.classList.toggle("dark", theme === "dark");
}

function readLocale(): Locale {
  const stored = localStorage.getItem(LOCALE_KEY);
  return isLocale(stored) ? stored : DEFAULT_LOCALE;
}

export function PrefsProvider({ children }: { children: React.ReactNode }) {
  const pathname = usePathname() || "/";
  const [locale, setLocaleState] = useState<Locale>(DEFAULT_LOCALE);
  const [theme, setThemeState] = useState<Theme>("light");

  useLayoutEffect(() => {
    const storedTheme =
      (localStorage.getItem(THEME_KEY) as Theme | null) ||
      (window.matchMedia("(prefers-color-scheme: dark)").matches ? "dark" : "light");
    const loc = readLocale();
    const th: Theme = storedTheme === "dark" ? "dark" : "light";
    setLocaleState(loc);
    setThemeState(th);
    applyDom(loc, th);
  }, []);

  useLayoutEffect(() => {
    document.title = tabTitle(locale, pathname);
  }, [locale, pathname]);

  const setLocale = useCallback((next: Locale) => {
    setLocaleState(next);
    localStorage.setItem(LOCALE_KEY, next);
    applyDom(next, document.documentElement.classList.contains("dark") ? "dark" : "light");
  }, []);

  const setTheme = useCallback((next: Theme) => {
    setThemeState(next);
    localStorage.setItem(THEME_KEY, next);
    applyDom(readLocale(), next);
  }, []);

  const toggleTheme = useCallback(() => {
    setThemeState((cur) => {
      const next: Theme = cur === "dark" ? "light" : "dark";
      localStorage.setItem(THEME_KEY, next);
      applyDom(readLocale(), next);
      return next;
    });
  }, []);

  const value = useMemo<Prefs>(() => {
    const meta = localeMeta(locale);
    return {
      locale,
      dir: meta.dir,
      intl: meta.intl,
      theme,
      t: dictionaries[locale] ?? dictionaries[DEFAULT_LOCALE],
      setLocale,
      setTheme,
      toggleTheme,
      tf: interpolate,
    };
  }, [locale, theme, setLocale, setTheme, toggleTheme]);

  return (
    <PrefsContext.Provider value={value}>
      <MotionConfig reducedMotion="user">{children}</MotionConfig>
    </PrefsContext.Provider>
  );
}

export function usePrefs() {
  const ctx = useContext(PrefsContext);
  if (!ctx) throw new Error("usePrefs must be used within PrefsProvider");
  return ctx;
}
